import React, { useEffect, useState } from "react";
import { api, formatCHF } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  aktiv: true,
  standard_stundenlohn: 25.0,
  standard_ft: null,
  standard_qm: null,
  verladung_bezahlen: false,
  entladung_bezahlen: false,
  km_verguetung: false,
  km_satz: 0.8,
  bemerkungen: "",
};

export default function Mitarbeiter() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const r = await api.get("/employees");
    setList(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ ...EMPTY, ...emp });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        standard_stundenlohn: Number(form.standard_stundenlohn) || 0,
        km_satz: Number(form.km_satz) || 0,
        standard_ft: form.standard_ft === "" || form.standard_ft === null ? null : Number(form.standard_ft),
        standard_qm: form.standard_qm === "" || form.standard_qm === null ? null : Number(form.standard_qm),
      };
      if (editing) {
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Mitarbeiter aktualisiert");
      } else {
        await api.post("/employees", payload);
        toast.success("Mitarbeiter angelegt");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Mitarbeiter wirklich löschen?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <PageContainer>
      <PageHeader title="Mitarbeiter" subtitle="Stammdaten Fahrer und Vergütungsregeln" testId="mitarbeiter-page">
        <Button onClick={openNew} data-testid="add-employee-button" className="bg-[#0055FF] hover:bg-[#0040CC]">
          <Plus className="h-4 w-4 mr-2" /> Neuer Mitarbeiter
        </Button>
      </PageHeader>

      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="mitarbeiter-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Aktiv</TableHead>
                <TableHead className="text-right">Stundenlohn</TableHead>
                <TableHead className="text-right">FT Lohn</TableHead>
                <TableHead className="text-right">QM Lohn</TableHead>
                <TableHead>Verladung/Entladung</TableHead>
                <TableHead>KM-Vergütung</TableHead>
                <TableHead className="text-right">KM-Satz</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-neutral-500 py-8">
                    Noch keine Mitarbeiter erfasst.
                  </TableCell>
                </TableRow>
              )}
              {list.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
                  <TableCell className="font-medium">{e.name}</TableCell>
                  <TableCell>
                    <Badge variant={e.aktiv ? "default" : "secondary"} className={e.aktiv ? "bg-[#008A00] text-white" : ""}>
                      {e.aktiv ? "Aktiv" : "Inaktiv"}
                    </Badge>
                  </TableCell>
                  <TableCell className="nivara-num">{formatCHF(e.standard_stundenlohn)}</TableCell>
                  <TableCell className="nivara-num">{e.standard_ft != null ? formatCHF(e.standard_ft) : "—"}</TableCell>
                  <TableCell className="nivara-num">{e.standard_qm != null ? formatCHF(e.standard_qm) : "—"}</TableCell>
                  <TableCell className="text-xs">
                    {e.verladung_bezahlen ? "V: ✓" : "V: ✗"} · {e.entladung_bezahlen ? "E: ✓" : "E: ✗"}
                  </TableCell>
                  <TableCell>{e.km_verguetung ? "Ja" : "Nein"}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(e.km_satz)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(e)} data-testid={`edit-employee-${e.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(e.id)} data-testid={`delete-employee-${e.id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Standard-Stundenlohn (CHF)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.standard_stundenlohn}
                onChange={(e) => setForm({ ...form, standard_stundenlohn: e.target.value })}
                data-testid="employee-stundenlohn-input"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>Standard FT-Lohn (optional)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.standard_ft ?? ""}
                onChange={(e) => setForm({ ...form, standard_ft: e.target.value })}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>Standard QM-Lohn (optional)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.standard_qm ?? ""}
                onChange={(e) => setForm({ ...form, standard_qm: e.target.value })}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>KM-Satz Fahrer (CHF/km)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.km_satz}
                onChange={(e) => setForm({ ...form, km_satz: e.target.value })}
                className="font-mono"
              />
            </div>
            <div className="flex items-center justify-between border border-neutral-200 rounded-md p-3">
              <Label className="cursor-pointer">Aktiv</Label>
              <Switch checked={form.aktiv} onCheckedChange={(v) => setForm({ ...form, aktiv: v })} data-testid="employee-aktiv-switch" />
            </div>
            <div className="flex items-center justify-between border border-neutral-200 rounded-md p-3">
              <Label className="cursor-pointer">Verladung bezahlen</Label>
              <Switch checked={form.verladung_bezahlen} onCheckedChange={(v) => setForm({ ...form, verladung_bezahlen: v })} />
            </div>
            <div className="flex items-center justify-between border border-neutral-200 rounded-md p-3">
              <Label className="cursor-pointer">Entladung bezahlen</Label>
              <Switch checked={form.entladung_bezahlen} onCheckedChange={(v) => setForm({ ...form, entladung_bezahlen: v })} />
            </div>
            <div className="flex items-center justify-between border border-neutral-200 rounded-md p-3">
              <Label className="cursor-pointer">KM-Vergütung</Label>
              <Switch checked={form.km_verguetung} onCheckedChange={(v) => setForm({ ...form, km_verguetung: v })} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Bemerkungen</Label>
              <Textarea value={form.bemerkungen || ""} onChange={(e) => setForm({ ...form, bemerkungen: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="employee-cancel">Abbrechen</Button>
            <Button onClick={save} data-testid="employee-save" className="bg-[#0055FF] hover:bg-[#0040CC]">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
